import * as React from "react";
import { useState, useEffect, useCallback } from "react";
// @mui material components
import { Box, Grid, Chip, Button, Card, Typography, Tooltip, Alert, List, ListItem, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { DatePicker, DesktopTimePicker } from "@mui/x-date-pickers";
import Checkbox from "@mui/material/Checkbox";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import FormControlLabel from "@mui/material/FormControlLabel";
import Autocomplete from "@mui/material/Autocomplete";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import axios from "axios";
// Others
import Label from '../../label/Label';
import CustomInputLabel from "../../custom-input-label";
import customStyle from "../../customstyle.module.css";
import dayjs from "dayjs";

// Data
import { signatures_live as signatures } from "../../client-config";
import suggestedActionsData from "../../data/SuggestedActions";
// import arryhthmiaData from "layouts/manual-Interpretation/data/arryhthmiaData";
// import stemiData from "layouts/manual-Interpretation/data/stemiData";
// import arrhythmiaTextData from "layouts/manual-Interpretation/data/arrhythmiaTextData";
import stemiTextData from "../../data/stemiTextData";
import LeadTraceMatrixSection from "./LeadTraceMatrixSection";
import { toast } from "sonner";
import AxisCalculator from "./AxisCalculator";
import { stripEmojis } from "../../../../utils/emoji";

// import Typography from "theme/overrides/Typography";

function CustomReportInputForm({ formKey, displayData, inputData, handleReportInputFormSubmit, businessName, subsDetails, reportData }) {
  const [formInput, setFormInputs] = useState(inputData || {});
  const [errors, setErrors] = useState({});
  const [pdfLoading, setPdfLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [arrhythmiaSuggetions] = useState([]);
  const [leadTraceData, setLeadTraceData] = useState(inputData?.ecg_quality_matrix);

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  let expertPhoneNumber;
  const expertDetails = localStorage.getItem("TokenDetails") && JSON.parse(localStorage.getItem("TokenDetails"))
  if (expertDetails?.userType === "expert") {
    expertPhoneNumber = expertDetails?.phoneNumber;
  }
  useEffect(() => {
    setFormInputs(inputData || {});
    setLeadTraceData(inputData?.ecg_quality_matrix);
    setErrors({});
    setPdfLoading(false);
  }, [inputData]);


  useEffect(() => {
    // Check if there are any changes in form values
    const isFormChanged = Object.keys(formInput || {}).some(
      key => formInput[key] !== inputData?.[key]
    );
    setIsFormChanged(isFormChanged);
    if (isFormChanged) {
      // console.log("Form changed")
    }

  }, [formInput, inputData]);

  function readFileAsync(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = function (event) {
        resolve(event.target.result);
      };

      reader.onerror = function (event) {
        reject(event.target.error);
      };

      reader.readAsDataURL(file);
    });
  }

  const handleChange = async (event) => {
    const name = event.target.name;
    // Emojis are not accepted in any field.
    let value = stripEmojis(event.target.value);
    const numericFields = [
      "pr_interval_box",
      "qrs_interval_box",
      "qt_interval_box",
      "st_interval_box",
      "rr_interval_box",
      "total_score",
    ];

    if (numericFields.includes(name)) {
      value = String(value)
        .replace(/[^\d.]/g, "")
        .replace(/(\..*)\./g, "$1");
    }

    setPdfLoading(false);
    setErrors((current) => ({ ...current, [name]: undefined }));

    if (name === "interpreted_report_file") {
      const file = event.target.files?.[0];
      if (!file) return;

      if (file.type !== "application/pdf") {
        setErrors((current) => ({ ...current, interpreted_report_file: "Please choose a PDF file." }));
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setErrors((current) => ({ ...current, interpreted_report_file: "The PDF must be 10 MB or smaller." }));
        return;
      }

      try {
        const base64String = await readFileAsync(file);
        setFormInputs((values) => ({
          ...values,
          interpreted_report_file: base64String,
          interpreted_report_file_metadata: {
            name: file.name,
            size: file.size,
            last_modified_at: file.lastModified,
            type: file.type,
          },
        }));
      } catch (error) {
        console.error("Error reading file:", error);
        setErrors((current) => ({ ...current, interpreted_report_file: "The selected file could not be read." }));
      }
      return;
    }

    setFormInputs((values) => {
      const nextValues = { ...values, [name]: value };

      if (name.includes("_box")) {
        const parsedValue = Number(value);
        nextValues[name.replaceAll("_box", "")] =
          value !== "" && Number.isFinite(parsedValue)
            ? String(Math.round(parsedValue * 40))
            : "";
      }

      if (name === "rr_interval_box") {
        const rrInterval = nextValues.rr_interval;
        nextValues.heart_rate = rrInterval ? String(Math.round(60000 / Number(rrInterval))) : "";
        nextValues.qtc = calculateQTC(nextValues.qt_interval, rrInterval);
      }

      if (name === "qt_interval_box") {
        nextValues.qtc = calculateQTC(nextValues.qt_interval, nextValues.rr_interval);
      }

      return nextValues;
    });

    if (name === "total_score") suggetionsFromStemi({ total_score: value });
  };

  const validateForm = () => {
    const nextErrors = {};
    const isPlaceholder = (value) => !value || value === "Choose Anyone" || value === "Choose Axis";

    if (isPlaceholder(formInput.nature_of_ecg)) {
      nextErrors.nature_of_ecg = "Select the ECG nature.";
    }

    if (
      formInput.nature_of_ecg &&
      formInput.nature_of_ecg !== "Normal ECG" &&
      isPlaceholder(formInput.classification_of_abnormality)
    ) {
      nextErrors.classification_of_abnormality = "Select the abnormality classification.";
    }

    ["pr_interval_box", "qrs_interval_box", "qt_interval_box", "st_interval_box", "rr_interval_box", "total_score"].forEach((field) => {
      const value = formInput[field];
      if (value !== undefined && value !== "" && (!Number.isFinite(Number(value)) || Number(value) < 0)) {
        nextErrors[field] = "Enter a valid non-negative number.";
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const numericFieldProps = (field) => ({
    inputProps: { inputMode: "decimal", min: 0, pattern: "[0-9]*[.]?[0-9]*" },
    error: Boolean(errors[field]),
    helperText: errors[field],
  });

  function calculateQTC(qt, rr) {
    if (qt && rr) {
      return parseInt((parseFloat(qt) / 1000 / Math.sqrt(parseFloat(rr) / 1000)) * 1000);
    } else {
      return "";
    }
  }

  const handleFindingsMultiSelect = (event, selectedOptions, reason) => {
    let result = null;
    if (reason === "clear") {
      suggestedActions(null, true);
    } else if (reason === "selectOption" || reason === "removeOption") {
      if (selectedOptions.find((option) => option.value === "select-all")) {
        result = selectedOptions.filter((option) => option.value !== "select-all");
      } else {
        result = selectedOptions;
      }
      suggestedActions(result);
    }
  };
  function suggestedActions(selectedValues, isRemove) {
    let sugg = formInput["suggested_actions"] || "";
    let resultText = "";
    //Remove All prv selected text
    const newLine = "\n";
    for (var value of suggestedActionsData) {
      sugg = sugg.replaceAll(`${value}${newLine}`, "");
    }
    if ((selectedValues || []).length)
      selectedValues.map((item) => {
        if (!sugg.match(item)) resultText += `${item}${newLine}`;
      });
    if (resultText !== "") {
      sugg += resultText;
    }
    setFormInputs((values) => ({
      ...values,
      ["suggested_actions"]: sugg,
      findings: selectedValues,
    }));
  }

  const handleArrhymiaChange = (event, selectedOptions, reason) => {
    commonAutoCompleteChange(event, selectedOptions, reason, "Arrhymia");
  };

  const handleStemiChange = (event, selectedOptions, reason) => {
    commonAutoCompleteChange(event, selectedOptions, reason, "Stemi");
  };

  function commonAutoCompleteChange(event, selectedOptions, reason, elKey) {
    let result = null;
    if (reason === "clear") {
      if (elKey === "Arrhymia") {
        suggetionsFromArrhymia();
      } else if (elKey === "Stemi") {
        suggetionsFromStemi();
      }
    } else if (reason === "selectOption" || reason === "removeOption") {
      if (selectedOptions.find((option) => option.value === "select-all")) {
        //handleToggleSelectAll();
        // let result = [];
        result = selectedOptions.filter((option) => option.value !== "select-all");
        // onChange(result);
      } else {
        //handleToggleOption(selectedOptions);
        result = selectedOptions;
        // onChange(selectedOptions);
      }
      const resultKey = {};
      result.map((item) => {
        resultKey[item.title] = true;
      });
      if (elKey === "Arrhymia") {
        suggetionsFromArrhymia(resultKey);
      } else if (elKey === "Stemi") {
        suggetionsFromStemi(resultKey);
      }
    }
  }

  function suggetionsFromArrhymia(resultKey) {
    let resultText = "";
    var sugg = formInput["suggested_actions"] || "";
    if (resultKey) {
      //1. Atrial fibrillation
      if (
        (resultKey["pWaveAbsent"] == true &&
          resultKey["rrVariabilityThresholdgreater25"] == true) ||
        resultKey["interpWaveRategreater250"] == true
      ) {
        resultText += "\n Atrial Fibrillation";
      }
      //2. Atrial Flutter
      if (resultKey["intraprategreater250_350"] == true) {
        resultText += "\n Atrial Flutter";
      }
      //3. Atrial Tachycardia
      // Check for atrial tachycardia based on the combined criteria
      if (
        resultKey["heartRateGreaterThan100"] == true &&
        resultKey["pWaveDurationBetween150And250"] == true &&
        resultKey["narrowQRS"] == true
      ) {
        resultText += "\n Atrial Tachycardia";
      }
      //4. Junctional Rhythm
      // Check for junctional rhythm based on the combined
      //criteria
      if (
        resultKey["pWaveAbsent"] == true &&
        resultKey["heartRateGreaterThan60"] == true &&
        resultKey["rrVariabilityLessThan10(rrVariability)"] == true &&
        resultKey["narrowQRS"] == true
      ) {
        resultText += "\n Junctional Rhythm";
      }
      //5. High Degree AV block
      // Check for high-degree AV block based on the combined criteria
      if (
        resultKey["prolongedPRI"] == true &&
        resultKey["rrVariabilityGreaterThan20(rrVariability)"] == true
      ) {
        resultText += "\n High-Degree AV Block";
      }
      //6. AV block
      // Check for AV block based on the combined criteria
      if (resultKey["prolongedPRI"] == true && resultKey["rrVariabilityLessThan20"] == true) {
        resultText += "\n AV Block";
      }
      //7. WPW syndrome(pre - excitation)
      // Check for Wolff-Parkinson-White (WPW) syndrome based on the combined
      //criteria
      if (resultKey["shortenedPRInterval"] && resultKey["lurredRWave"] && resultKey["wideQRS"]) {
        resultText += "\n Wolff-Parkinson-White (WPW) Syndrome";
      }
      //8. SuperaVenticular Ectopics
      // Check for superaventricular ectopics based on the combined criteria
      if (resultKey["rrVariabilityGreaterThan25"] == true && resultKey["narrowQRS"] == true) {
        resultText += "\n Superaventricular Ectopics";
      }
      //9. Ventricular Ectopics
      // Check for ventricular ectopics based on the combined criteria
      if (resultKey["rrVariabilityGreaterThan25"] == true && resultKey["wideQRS"] == true) {
        resultText += "\n Ventricular Ectopics";
      }
      //10.Superaventricular Tachycardia
      // Check for Supraventricular Tachycardia (SVT) based on the combined
      //criteria
      if (
        resultKey["heartRateGreaterThan100"] &&
        resultKey["absentPWave"] &&
        resultKey["narrowQRS"]
      ) {
        resultText += "\n Supraventricular Tachycardia";
      }
      //11.Ventricular tachycardia
      // Check for Ventricular Tachycardia based on the combined criteria
      if (
        resultKey["heartRateGreaterThan100"] &&
        resultKey["absentPWave"] &&
        resultKey["wideQRS"]
      ) {
        resultText += "\n Ventricular Tachycardia";
      }
      //12.Ventricular premature Complex
      // Check for Ventricular Premature Contraction (VPC) based on the combined
      //criteria
      if (
        resultKey["superimposedPWave"] &&
        resultKey["stDiscordant"] &&
        resultKey["rrVariabilityGreaterThan25"] &&
        resultKey["wideQRS"]
      ) {
        resultText += "\n Ventricular Premature Contraction (VPC) Detected";
      }
      //13.Sinus Bradycardia
      // Check for Sinus Bradycardia based on the criteria
      if (resultKey["heartRateLessThan60"]) {
        resultText += "\n Sinus Bradycardia";
      }
      //14.Sinus Tachycarida
      // Check for Sinus Bradycardia based on the criteria
      if (resultKey["heartRategGreaterThan60"]) {
        resultText += "\n Sinus Tachycardia";
      }
    } else {
      resultText = "";
    }
    //Remove All prv selected text
    for (var value of arrhythmiaSuggetions) {
      sugg = sugg.replaceAll("\n " + value, "");
    }
    if (resultText !== "") {
      sugg += resultText;
    }
    setFormInputs((values) => ({ ...values, ["suggested_actions"]: sugg }));
  }

  function suggetionsFromStemi(resultKey) {
    let resultText = "";
    var sugg = formInput["suggested_actions"] || "";
    if (resultKey) {
      //1. Left bundle Branch block
      // Check for Left Bundle Branch Block (LBBB) based on the combined criteria
      if (resultKey["wideQRS"] && resultKey["dominantSV1"] && resultKey["notchedMShapeRWaveV5V6"]) {
        resultText += "\n Left Bundle Branch Block (LBBB)";
      }
      //2. Right Branch Bundle Block
      // Check for Right Bundle Branch Block (RBBB) based on the combined criteria
      if (resultKey["rBySRatioGreaterThan1InV1"] && resultKey["wideQRS"]) {
        resultText += "\n Right Bundle Branch Block (RBBB)";
      }
      //3. Anterior Wall MI
      // Check for Anterior Wall Myocardial Infarction (MI) based on the combined criteria
      if (
        resultKey["stElevationOrDepressionInV1"] &&
        resultKey["stElevationOrDepressionInV2"] &&
        resultKey["stElevationOrDepressionInV3"]
      ) {
        resultText += "\n Anterior Wall Myocardial Infarction (MI)";
      }
      //4. Antero - septal MI
      // Check for Antero-septal Myocardial Infarction (MI) based on the combined criteria
      if (
        resultKey["stElevationOrDepressionInV1"] &&
        resultKey["stElevationOrDepressionInV2"] &&
        resultKey["stElevationOrDepressionInV3"] &&
        resultKey["stElevationOrDepressionInV4"] &&
        resultKey["stElevationOrDepressionInV5"]
      ) {
        resultText += "\n Antero-septal Myocardial Infarction (MI)";
      }
      //5. Antero - Apical MI
      // Check for Antero-apical Myocardial Infarction (MI) based on the combined criteria
      if (
        resultKey["stElevationOrDepressionInLeadI"] &&
        resultKey["stElevationOrDepressionInLeadAVL"] &&
        resultKey["stElevationOrDepressionInV2"] &&
        resultKey["stElevationOrDepressionInV3"] &&
        resultKey["stElevationOrDepressionInV4"]
      ) {
        resultText += "\n Antero-apical Myocardial Infarction (MI)";
      }
      //6. Inferior wall MI
      // Check for Inferior Wall Myocardial Infarction (MI) based on the combined criteria
      if (
        resultKey["stElevationOrDepressionInLeadII"] &&
        resultKey["stElevationOrDepressionInLeadIII"] &&
        resultKey["stElevationOrDepressionInLeadAVF"]
      ) {
        resultText += "\n Inferior Wall Myocardial Infarction (MI)";
      }
      //7. Inferolateral MI
      // Check for Infero - lateral Wall Myocardial Infarction(MI) based on the combined criteria
      if (
        resultKey["stElevationOrDepressionInLeadII"] &&
        resultKey["stElevationOrDepressionInLeadIII && stElevationOrDepressionInLeadAVF"] &&
        resultKey["stElevationOrDepressionInLeadI"] &&
        resultKey["§stElevationOrDepressionInLeadAVL"]
      ) {
        resultText += "\n Infero-lateral Wall Myocardial Infarction (MI)";
      }

      //8. Lateral MI
      // Check for Lateral Wall Myocardial Infarction (MI) based on the combined criteria
      if (
        resultKey["stElevationOrDepressionInLeadI"] &&
        resultKey["stElevationOrDepressionInLeadAVL"] &&
        resultKey["stElevationOrDepressionInV5"] &&
        resultKey["stElevationOrDepressionInV6"]
      ) {
        resultText += "\n Lateral Wall Myocardial Infarction (MI) Detected";
      }
      //9. Ischemia
      // Check for ischemia based on the criteria
      if (resultKey["stSegmentDepression"] || resultKey["tWaveInversion"]) {
        resultText += "\n Ischemia";
      }
      //10.Left Ventricular Hypertrophy
      // Define the threshold for LVH diagnosis
      //int lvhThreshold = 5; //
      //Check for LVH based on the total score
      if (resultKey["total_score"] > 5) {
        resultText += "\n Left Ventricular Hypertrophy(LVH)";
      }
      //11.ST abnormality
      // Check for ST-T abnormality based on the criteria
      if (resultKey["stSegmentElevation"]) {
        resultText += "\n ST-T Abnormality";
      }
    } else {
      resultText = "";
    }
    //Remove All prv selected text
    for (var value of stemiTextData) {
      sugg = sugg.replaceAll("\n " + value, "");
    }
    if (resultText !== "") {
      sugg += resultText;
    }
    setFormInputs((values) => ({ ...values, ["suggested_actions"]: sugg }));
  }

  function onGenerateReportClick(e) {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please correct the highlighted report fields before continuing.");
      return;
    }

    if (isFormChanged) {
      setFormInputs({
        ...formInput,
        // date_of_receiving_ecg: formInput?.date_of_receiving_ecg || dayjs(parseInt(formInput?.requested_at)).format("DD/MM/YYYY"),
        // time_of_receiving_ecg: formInput?.time_of_receiving_ecg || dayjs(parseInt(formInput?.requested_at)).format("hh:mm A"),
        time_of_ecg_interpretation: formInput?.time_of_ecg_interpretation || dayjs().format("hh:mm A"),
        // signature_of: formInput?.signature_of || ((signatures || []).length > 0 ?
        //   (businessName || (subsDetails?.subscription_type?.toLowerCase() === "trial" ||  subsDetails?.subscription_type?.toLowerCase() === "paid") ? (signatures.find(item => item["name"] === "Dr. Christianez Ratna") || signatures.find(item => item?.default))
        //   : signatures.find(item => item["phoneNumber"] === expertPhoneNumber) || signatures.find(item => item?.default))["value"]
        //   : null),
        report_status: formInput["report_status"] || "normal"
      });
      setIsDialogOpen(true);
    } else {
      toast.info("There is no update to save.");
    }
  }

  async function onConfirmSubmitClick(e) {
    setPdfLoading(true);

    const ecg_quality_matrix = {
      ...(leadTraceData || getDefaultLeadMetricesData()),
      ...otherMatrixDataMapping()
    }
    const ppInterval = Number(formInput.pp_interval);
    const rrInterval = Number(formInput.rr_interval);

    const atrial_rate =
      ppInterval && ppInterval > 0 ? Math.round(60000 / ppInterval) : null;

    const ventricular_rate =
      rrInterval && rrInterval > 0 ? Math.round(60000 / rrInterval) : null;

    const derivedValues = {
      ...(atrial_rate !== null ? { atrial_rate } : {}),
      ...(ventricular_rate !== null ? { ventricular_rate } : {}),
    };

    try {
      await handleReportInputFormSubmit(e, {
        ...formInput,
        ...derivedValues,
        setPdfLoading,
        time_of_ecg_interpretation: formInput?.time_of_ecg_interpretation || dayjs().format("hh:mm A"),
        report_status: formInput["report_status"] || "normal",
        ecg_quality_matrix,
      });
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Unable to save custom interpretation report:", error);
      toast.error("The report could not be saved. Please try again.");
    } finally {
      setPdfLoading(false);
    }
  }

  const onCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
  });

  const otherMatrixDataMapping = () => {
    let otherMatrices = {
      r_peaks: [],
      lbbb: false,
      st_elevation: false,
      st_dipression: false,
      mi_all: false
    }
    if (reportData) otherMatrices.r_peaks = reportData?.characteristics?.r_peak_points ?? []
    if (formInput?.classification_of_abnormality === "LBBB") otherMatrices.lbbb = true
    if (formInput?.classification_of_abnormality === "ST-T Changes") otherMatrices.st_elevation = true
    if (formInput?.classification_of_abnormality === "Myocardial Infraction") otherMatrices.mi_all = true
    if (formInput?.findings?.includes("ST- depression and T-wave inversion")) otherMatrices.st_dipression = true

    return otherMatrices
  }

  const metrices = ['normal', 'noise', 'baseline_wandering', 'loose_electrodes', 'lead_reversal', 'garbage']
  const notNormalMetrices = ["garbage"]
  // Lead trace metrix default data
  const getDefaultLeadMetricesData = () => {
    const leadOrder = [
      'lead1', 'lead2', 'lead3',
      'avr', 'avl', 'avf',
      'v1', 'v2', 'v3', 'v4', 'v5', 'v6'
    ];

    // Sort using the prefix before "_data"
    const leadKeys = (reportData?.leads || []).sort((a, b) => {
      const aKey = a.replace('_data', '');
      const bKey = b.replace('_data', '');
      return leadOrder.indexOf(aKey) - leadOrder.indexOf(bKey);
    });

    let lead_trace_quality = {}

    leadKeys.forEach((lead) => {
      const leadKey = `${lead}`;
      lead_trace_quality[leadKey] = {
        ...metrices.reduce((acc, type, i) => {
          acc[type] = i === 0; // default: normal = true
          return acc;
        }, {})
      };
    });

    return { lead_trace_quality, lead_2_q_pathalogical_wave: false };
  }

  const onLeadTraceMatrixUpdate = (updatedData) => {
    setLeadTraceData(updatedData)
  }

  return (
    <form
      id="report-input-form"
      className={customStyle.reportForm}
      onSubmit={onGenerateReportClick}
    >
      <Grid
        container
        spacing={2}
        style={{ position: "relative" }}
        margin={0}
        width="100%"
        sx={{ pb: 2 }}
      >
        {/* <div style={{ padding: '15px', position: 'sticky', top: 0, zIndex: 99999, width: '100%' }}> */}
        <Grid item size={12}>
          <Box className={customStyle.formHeader}>
            <Box>
              <Typography className={customStyle.formTitle}>ECG report</Typography>
              <Typography variant="body2" color="text.secondary">
                Complete the interpretation details, review the calculated values, then generate the report.
              </Typography>
            </Box>
            <Box className={customStyle.reportTimestamp}>
              <CustomInputLabel>
                {formInput?.completed_at ? "Report generated at:" : "Report taken at:"}{" "}
                <b>{dayjs(parseInt(formInput?.completed_at || displayData?.report_timestamp)).format("DD MMM, YYYY hh:mm A")}</b>
              </CustomInputLabel>
            </Box>
          </Box>
        </Grid>
        {/* </div> */}
        {/* <Card className={customStyle.dotedBorder}> */}
        {/* <Grid container style={{ padding: "15px" }} spacing={2}> */}
        {/* <Grid item size={12}>
              <FormControl fullWidth>
                <DatePicker
                  key={`receive-date-${formKey}`}
                  label="Date of Receiving ECG"
                  name="date_of_receiving_ecg"
                  maxDate={dayjs()}
                  value={
                    formInput?.date_of_receiving_ecg
                      ? dayjs(formInput?.date_of_receiving_ecg, "DD/MM/YYYY")
                      : dayjs(parseInt(formInput?.requested_at))
                  }
                  format="DD/MM/YYYY"
                  closeOnSelect={true}
                  onAccept={(event) =>
                    handleChange({
                      target: {
                        name: "date_of_receiving_ecg",
                        value: dayjs(event).format("DD/MM/YYYY"),
                      },
                    })
                  }
                />
              </FormControl>
            </Grid> */}
        {/* <Grid item size={6}>
              <FormControl fullWidth>
                <DesktopTimePicker
                  key={`receive-time-${formKey}`}
                  label="Time of Receiving ECG"
                  openTo="hours"
                  size="small"
                  value={
                    formInput?.time_of_receiving_ecg
                      ? dayjs(formInput?.time_of_receiving_ecg, "hh:mm A")
                      : dayjs(parseInt(formInput?.requested_at))
                  }
                  onAccept={(event) =>
                    handleChange({
                      target: { name: "time_of_receiving_ecg", value: dayjs(event).format("hh:mm A") },
                    })
                  }
                />
              </FormControl>
            </Grid> */}
        {/* <Grid item size={6}>
              <FormControl fullWidth>
                <DesktopTimePicker
                  key={`gen-time-${formKey}`}
                  label="Time of ECG Interpretation"
                  openTo="hours"
                  value={
                    formInput?.time_of_ecg_interpretation
                      ? dayjs(formInput?.time_of_ecg_interpretation, "hh:mm A")
                      : dayjs()
                  }
                  onAccept={(event) =>
                    handleChange({
                      target: {
                        name: "time_Of_ecg_interpretation",
                        value: dayjs(event).format("hh:mm A"),
                      },
                    })
                  }
                />
              </FormControl>
            </Grid> */}
        {/* <Grid item size={12}>
              <Box sx={{ display: 'flex', padding: "5px 0", flexDirection: 'column', justifyContent: 'space-between', backgroundColor: "warning.lighter" }}>
                {businessName && (<Box width='50%'>
                  <Label
                    color='warning'
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      padding: '0 10px',
                      m: 1
                    }}>
                    <CustomInputLabel>
                      {businessName && `Business Name: ${businessName}`}
                    </CustomInputLabel>
                  </Label>
                </Box>)}
                {subsDetails && (<Box>
                  <Chip
                    label={`${subsDetails.subscription_type} - ${subsDetails.plan_name}`}
                    size="small"
                    color={subsDetails?.subscription_type?.toLowerCase() === "paid" ? 'success' : 'warning'}
                    sx={{ marginLeft: 1, fontSize: '10px', marginTop: 0, textTransform: 'capitalize' }}
                  />
                </Box>)}
              </Box>
            </Grid> */}

        {/* <Grid item size={6}>
              <TextField variant="outlined"
                fullWidth
                label="First Name"
                name="first_name"
                value={formInput?.first_name || ""}
                onChange={handleChange}
                size="small" />
            </Grid>
            <Grid item size={6}>
              <TextField variant="outlined"
                fullWidth
                label="Last Name"
                name="last_name"
                value={formInput?.last_name || ""}
                onChange={handleChange}
                size="small" />
            </Grid>
            <Grid item size={6}>
              <TextField variant="outlined"
                fullWidth
                label="Age"
                name="age"
                value={formInput?.age}
                onChange={handleChange}
                size="small" />
            </Grid>
            <Grid item size={6}>
              <TextField fullWidth
                select
                name="gender"
                label="Gender"
                value={formInput?.gender || "-"}
                SelectProps={{
                  native: true,
                }}
                onChange={handleChange}
                size="small"
              >
                <option key="-" value="-" selected disabled>Select</option>
                <option key="male" value="male" >Male</option>
                <option key="female" value="female" >Female</option>
                <option key="other" value="other" >Other</option>
              </TextField>
            </Grid> */}
        {/* </Grid> */}
        {/* </Card> */}

        <Card
          className={customStyle.dotedBorder}
          sx={{ width: "100%", border: "1px solid var(--app-border)", boxShadow: "none" }}
        >
          <Grid container style={{ padding: "15px" }} spacing={2} >
            <Grid item size={12}>
              <Typography sx={{ fontWeight: 800, fontSize: 16, color: "var(--app-text)" }}>
                Clinical classification
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Record the symptom profile and ECG classification before generating the report.
              </Typography>
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
              <TextField fullWidth
                select
                name="any_symptom"
                label="Any Symptoms"
                value={formInput["any_symptom"] || "Choose Anyone"}
                SelectProps={{
                  native: true,
                }}
                onChange={handleChange}
                size="small"
              >
                <option key="Choose Anyone" value="Choose Anyone" selected disabled>Choose Anyone</option>
                <option key="Chest Pain" value="Chest Pain" >Chest Pain</option>
                <option key="Shortness of Breath" value="Shortness of Breath" >Shortness of Breath</option>
                <option key="Any Other" value="Any Other" >Any Other</option>
                <option key="No Symptoms" value="No Symptoms" >No Symptoms</option>

              </TextField>

            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <TextField fullWidth
                select
                name="nature_of_ecg"
                label="Nature Of ECG"
                value={formInput["nature_of_ecg"] || "Choose Anyone"}
                error={Boolean(errors.nature_of_ecg)}
                helperText={errors.nature_of_ecg}
                SelectProps={{
                  native: true,
                }}
                onChange={handleChange}
                size="small"
              >
                <option key="Choose Anyone" value="Choose Anyone" selected disabled>Choose Anyone</option>
                <option key="Normal ECG" value="Normal ECG" >Normal ECG</option>
                <option key="Abnormal ECG" value="Abnormal ECG" >Abnormal ECG</option>
                <option key="Critical ECG" value="Critical ECG" >Critical ECG</option>
                <option key="Beyond Critical" value="Beyond Critical" >Beyond Critical</option>

              </TextField>
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <TextField fullWidth
                select
                name="classification_of_abnormality"
                label="Classification of Abnormality"
                value={formInput["classification_of_abnormality"] || "Choose Anyone"}
                error={Boolean(errors.classification_of_abnormality)}
                helperText={errors.classification_of_abnormality}
                SelectProps={{
                  native: true,
                }}
                onChange={handleChange}
                size="small"
              >
                <option key="Choose Anyone" value="Choose Anyone" selected disabled>Choose Anyone</option>
                <option key="Arrhythmia" value="Arrhythmia" >Arrhythmia</option>
                <option key="Ischemia" value="Ischemia" >Ischemia</option>
                <option key="Myocardial Infraction" value="Myocardial Infraction" >Myocardial Infraction</option>
                <option key="ST-T Changes" value="ST-T Changes" >ST-T Changes</option>
                <option key="J Point Elevation" value="J Point Elevation" >J Point Elevation</option>
                <option key="RBBB" value="RBBB" >RBBB</option>
                <option key="LBBB" value="LBBB" >LBBB</option>
                <option key="None" value="None" >None</option>

              </TextField>

            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <TextField variant="outlined"
                fullWidth
                label="Total Score"
                name="total_score"
                value={formInput["total_score"] || ""}
                onChange={handleChange}
                size="small"
                inputProps={{ inputMode: "decimal", min: 0 }}
                error={Boolean(errors.total_score)}
                helperText={errors.total_score} />
            </Grid>
          </Grid>
        </Card>
        <Card className={customStyle.dotedBorder} sx={{ width: "100%" }}>

          <Grid container style={{ padding: "15px" }} spacing={2}>
            <Grid item size={12}>
              <Typography sx={{ fontWeight: 800, fontSize: 16, color: "var(--app-text)" }}>
                Findings and measurements
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Add findings, then enter box counts to calculate the ECG intervals automatically.
              </Typography>
            </Grid>
            <Grid size={12}>

              <Autocomplete
                multiple
                id={`choose-findings-${formKey}`}
                onChange={handleFindingsMultiSelect}
                name="findings"
                value={formInput["findings"] || []}
                options={suggestedActionsData}
                disableCloseOnSelect
                getOptionLabel={(option) => option}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option}
                  </li>
                )}
                renderInput={(params) => {
                  return <TextField {...params} name="ArrhythmiaKey" label="Select Finding(s) from List" placeholder="Choose Multiple" />;
                }}
              />

            </Grid>
            <Grid item size={12}>
              <TextField variant="outlined"
                name="suggested_actions"
                label="Suggested Actions"
                value={formInput["suggested_actions"] || ""}
                onChange={handleChange}
                placeholder="Type your suggested action"
                multiline
                fullWidth
                rows={2}
                size="small" />

            </Grid>
            {/* Start PR Interval element */}
            <Grid item size={4}>
              <TextField variant="outlined"
                fullWidth
                label="PR Interval Box"
                name="pr_interval_box"
                placeholder="Eg: 4.2"
                value={formInput["pr_interval_box"] || ""}
                onChange={handleChange}
                size="small"
                {...numericFieldProps("pr_interval_box")}
              />

            </Grid>
            <Grid item size={2}>
              <Typography mt={1} variant="caption" display="block" gutterBottom>
                X 40 =
              </Typography>


            </Grid>
            <Grid item size={4}>
              <TextField variant="outlined"
                fullWidth
                label="PR Interval"
                name="pr_interval"
                InputProps={{ readOnly: true }}
                inputProps={{ tabIndex: -1 }}
                placeholder="PR Interval"
                value={formInput["pr_interval"] || ""}
                onChange={handleChange}
                size="small"
              />
            </Grid>
            <Grid item size={2}>
              <Typography mt={1} variant="caption" display="block" gutterBottom>
                ms
              </Typography>
            </Grid>
            {/* End Start PR Interval element */}

            {/* Start QRS Interval element */}
            <Grid item size={4}>
              <TextField variant="outlined"
                fullWidth
                label="QRS Interval Box"
                name="qrs_interval_box"
                placeholder="Eg: 4.2"
                value={formInput["qrs_interval_box"] || ""}
                onChange={handleChange}
                size="small"
                {...numericFieldProps("qrs_interval_box")}
              />
            </Grid>
            <Grid item size={2}>
              <Typography mt={1} variant="caption" display="block" gutterBottom>
                X 40 =
              </Typography>
            </Grid>
            <Grid item size={4}>
              <TextField variant="outlined"
                fullWidth
                label="QRS Interval"
                name="qrs_interval"
                InputProps={{ readOnly: true }}
                inputProps={{ tabIndex: -1 }}
                placeholder="QRS Interval"
                value={formInput["qrs_interval"] || ""}
                onChange={handleChange}
                size="small"
              />
            </Grid>
            <Grid item size={2}>
              <Typography mt={1} variant="caption" display="block" gutterBottom>
                ms
              </Typography>
            </Grid>
            {/* End QRS Interval element */}

            {/* Start QT Interval element */}
            <Grid item size={4}>
              <TextField variant="outlined"
                fullWidth
                label="QT Interval Box"
                name="qt_interval_box"
                placeholder="Eg: 4.2"
                value={formInput["qt_interval_box"] || ""}
                onChange={handleChange}
                size="small"
                {...numericFieldProps("qt_interval_box")}
              />
            </Grid>
            <Grid item size={2}>
              <Typography mt={1} variant="caption" display="block" gutterBottom>
                X 40 =
              </Typography>
            </Grid>
            <Grid item size={4}>
              <TextField variant="outlined"
                fullWidth
                label="QT Interval"
                name="qt_interval"
                InputProps={{ readOnly: true }}
                inputProps={{ tabIndex: -1 }}
                placeholder="QTInterval"
                value={formInput["qt_interval"] || ""}
                onChange={handleChange}
                size="small"
              />
            </Grid>
            <Grid item size={2}>
              <Typography mt={1} variant="caption" display="block" gutterBottom>
                ms
              </Typography>
            </Grid>
            {/* End QT Interval element */}

            {/* Start ST Interval element */}
            <Grid item size={4}>
              <TextField variant="outlined"
                fullWidth
                label="ST Interval Box"
                name="st_interval_box"
                placeholder="Eg: 4.2"
                value={formInput["st_interval_box"] || ""}
                onChange={handleChange}
                size="small"
                {...numericFieldProps("st_interval_box")}
              />
            </Grid>
            <Grid item size={2}>
              <Typography mt={1} variant="caption" display="block" gutterBottom>
                X 40 =
              </Typography>
            </Grid>
            <Grid item size={4}>
              <TextField variant="outlined"
                fullWidth
                label="ST Interval"
                name="st_interval"
                InputProps={{ readOnly: true }}
                inputProps={{ tabIndex: -1 }}
                placeholder="ST Interval"
                value={formInput["st_interval"] || ""}
                onChange={handleChange}
                size="small"
              />
            </Grid>
            <Grid item size={2}>
              <Typography mt={1} variant="caption" display="block" gutterBottom>
                ms
              </Typography>
            </Grid>
            {/* End ST Interval element */}

            {/* Start RR Interval element */}
            <Grid item size={4}>
              <TextField variant="outlined"
                fullWidth
                label="RR Interval Box"
                name="rr_interval_box"
                placeholder="Eg: 4.2"
                value={formInput["rr_interval_box"] || ""}
                onChange={handleChange}
                size="small"
                {...numericFieldProps("rr_interval_box")}
              />
            </Grid>
            <Grid item size={2}>
              <Typography mt={1} variant="caption" display="block" gutterBottom>
                X 40 =
              </Typography>
            </Grid>
            <Grid item size={4}>
              <TextField variant="outlined"
                fullWidth
                label="RR Interval"
                name="rr_interval"
                InputProps={{ readOnly: true }}
                inputProps={{ tabIndex: -1 }}
                placeholder="RR Interval"
                value={formInput["rr_interval"] || ""}
                onChange={handleChange}
                size="small"
              />
            </Grid>
            <Grid item size={2}>
              <Typography mt={1} variant="caption" display="block" gutterBottom>
                ms
              </Typography>
            </Grid>
          </Grid>

        </Card>


        <Card className={customStyle.dotedBorder} sx={{ width: '100%' }}>
          <Grid container style={{ padding: "15px" }} spacing={2}>
            <Grid item size={12}>
              <Typography sx={{ fontWeight: 800, fontSize: 16, color: "var(--app-text)" }}>
                Calculated ECG values
              </Typography>
            </Grid>
            <Grid item size={6}>
              <Typography mt={1} variant="caption" gutterBottom>
                Heart Rate = <b>{formInput["heart_rate"] || ""}</b>
                <br /> <em>[HR = 60000 / RR (ms)]</em>
              </Typography>
            </Grid>
            <Grid item size={6}>
              <TextField display="block" fullWidth
                select
                name="heart_rate_axis"
                label="Heart Rate Axis"
                value={formInput["heart_rate_axis"] || "Choose Axis"}
                SelectProps={{
                  native: true,
                }}
                onChange={handleChange}
                size="small"
              >
                <option key="Choose Axis" value="Choose Axis" selected disabled>Choose Anyone</option>
                <option key="Normal" value="Normal" >Normal</option>
                <option key="Left Axis Deviation" value="Left Axis Deviation" >Left Axis Deviation</option>
                <option key="Right Axis Deviation" value="Right Axis Deviation" >Right Axis Deviation</option>
                <option key="Extreme Axis" value="Extreme Axis" >Extreme Axis</option>
                <option key="-" value="-" >NA</option>
              </TextField>
            </Grid>
            <Grid item size={6}>
              <Typography mt={1} variant="caption" sx={{ width: '100%' }} gutterBottom>
                QTc = QT / √RR (ms) = <b>{formInput["qtc"] || ""}</b>
              </Typography>
            </Grid>
          </Grid>
          <AxisCalculator
            initialValues={formInput}
            onValuesChange={(vals) => setFormInputs(prev => ({ ...prev, ...vals }))}
          />
        </Card>

        <Card className={customStyle.dotedBorder} sx={{ width: "100%" }}>
          <LeadTraceMatrixSection
            leadKeys={reportData?.leads || []}
            metrices={metrices}
            notNormalMetrices={notNormalMetrices}
            initialValues={inputData?.ecg_quality_matrix || getDefaultLeadMetricesData()}
            onChange={(updatedData) => onLeadTraceMatrixUpdate(updatedData)}
          />
        </Card>

        <Card className={customStyle.dotedBorder} sx={{ width: "100%" }}>
          <Grid container style={{ padding: "15px" }} spacing={2}>
            <Grid item size={12}>
              <Typography sx={{ fontWeight: 800, fontSize: 16, color: "var(--app-text)" }}>
                Report status
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Select the status that should be applied when the report is generated.
              </Typography>
            </Grid>
            {inputData?.interpreted_report_file_metadata?.name ? <Grid item size={12} textAlign="center">
              <Alert severity="success" sx={{ whiteSpace: 'break-word', wordBreak: 'break-all' }}>
                <Tooltip title={inputData?.interpreted_report_file_metadata?.name}>
                  <Typography component="div">
                    Uploaded report:
                    <b> {inputData?.interpreted_report_file_metadata?.name}</b>
                  </Typography>
                </Tooltip>
              </Alert>
            </Grid> : null}
            {/* <Grid item size={12}>
              <TextField
                name="interpreted_report_file"
                type="file"
                label="Upload interpreted report (optional)"
                fullWidth
                helperText={`Note: ${inputData?.interpreted_report_file_metadata?.name ? "Select to upload the updated report (if any)." : "If already interpreted then upload the report pdf."}`}
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: '.pdf' }}
                onChange={handleChange}
              />
            </Grid> */}
            <Grid item size={12} ></Grid>
            {/* <Grid item size={12}>
              <FormControl fullWidth>
                <CustomInputLabel>Select signature</CustomInputLabel>
                <Select
                  name="signature_of"
                  value={formInput?.signature_of || ((signatures || []).length > 0 ?
                    (businessName || (subsDetails?.subscription_type?.toLowerCase() === "trial" ||  subsDetails?.subscription_type?.toLowerCase() === "paid") ? (signatures.find(item => item["name"] === "Dr. Christianez Ratna") || signatures.find(item => item?.default))
                    : signatures.find(item => item["phoneNumber"] === expertPhoneNumber) || signatures.find(item => item?.default))["value"]
                    : null)
                  }
                  onChange={handleChange}
                  fullWidth
                  className={[customStyle.onFocus, "selectInputStyle"]}
                >
                  {(signatures || []).map((item, index) => (
                    <MenuItem key={`sign-${index}`} value={item.value} selected={item.default}>
                      {item.name}  
                    </MenuItem>
                  ))}
                </Select>

              </FormControl>


            </Grid> */}
            <Grid item size={12}>
              <div style={{ display: "flex" }}>
                <FormControl>
                  <CustomInputLabel>Select report type (for segregation)</CustomInputLabel>
                  <RadioGroup row name="report_status" aria-labelledby="demo-row-radio-buttons-group-label" onChange={handleChange} value={formInput["report_status"] || "normal"}>
                    <FormControlLabel value="normal" control={<Radio />} label="Normal report" />
                    <FormControlLabel value="abnormal" control={<Radio />} label="Abnormal report" />
                    <FormControlLabel value="critical" control={<Radio />} label="Critical report" />
                    <FormControlLabel value="beyond_critical" control={<Radio />} label="Beyond Critical" />
                    <FormControlLabel value="retake" control={<Radio />} label="Retake" />
                  </RadioGroup>
                </FormControl>
              </div>
            </Grid>
          </Grid>
        </Card>
      </Grid>
      <div className={customStyle.centerBtn}>
        <Button
          className={customStyle.generateButton}
          variant="contained"
          type="submit"
          color="info"
          disabled={pdfLoading}
        >
          {pdfLoading ? 'Loading....' : 'Save & Generate Report'}
        </Button>
      </div>
      <Dialog
        open={isDialogOpen}
        onClose={onCloseDialog}
        disableScrollLock={false}
        PaperProps={{ className: customStyle.confirmDialogPaper }}
      >
        <DialogTitle className={customStyle.confirmDialogTitle}>Review and generate report</DialogTitle>
        <DialogContent className={customStyle.confirmDialogContent}>
          <Typography className={customStyle.confirmDialogDescription}>
            Review the final report details before generating the ECG report.
          </Typography>
          <List className={customStyle.confirmSummary} disablePadding>
            {/* <ListItem key={0}>
              <b>• Signature:</b><ListItemText sx={{ pl: 2 }} primary={`${signatures.find(sign => sign?.value === formInput?.signature_of)?.name || 'Not selected'}`} />
            </ListItem> */}
            <ListItem key={1} className={customStyle.confirmSummaryRow}>
              <Typography component="span" className={customStyle.confirmSummaryLabel}>Report status</Typography>
              <ListItemText
                className={customStyle.confirmSummaryValue}
                primary={`${formInput?.report_status?.replaceAll("_", " ") || "Not selected"}`}
              />
            </ListItem>
          </List>
          <Typography variant="body1">
            Please confirm the details above are correct before proceeding.
          </Typography>
        </DialogContent>
        <DialogActions className={customStyle.confirmDialogActions}>
          <Button className={customStyle.confirmCancelButton} onClick={onCloseDialog} color="primary" variant="outlined">
            Cancel
          </Button>

          <Button className={customStyle.confirmSubmitButton} onClick={onConfirmSubmitClick} color="primary" variant="contained" disabled={pdfLoading} autoFocus >
            {pdfLoading ? 'Saving...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </form>
  );
}

export default CustomReportInputForm;
